// ---- sFlow network-traffic analysis --------------------------------------
// VMware-only surface (OpenNebula has no equivalent). 404 when sFlow is off.

import { useQuery } from '@tanstack/react-query';
import { serviceApi, vmPath, vmKeys, unwrap } from '../_shared';

export interface SFlowSummary {
  total_bandwidth_mbps?: number;
  total_bps?: number;
  total_bytes?: number;
  total_packets?: number;
  total_pps?: number;
  unique_destinations?: number;
  unique_sources?: number;
}

export interface SFlowLog {
  timestamp?: string;
  src_ip?: string;
  src_port?: number;
  dst_ip?: string;
  dst_port?: number;
  protocol?: number;
  bytes?: number;
  packets?: number;
  bps?: number;
  pps?: number;
}

export interface SFlowSource {
  src_ip?: string;
  connection_count?: number;
  total_bytes?: number;
  total_packets?: number;
  protocols?: number[];
  ports?: number[];
  latest_timestamp?: string;
}

/** Query params for `useInstanceSflow` — direction + flow count cap. */
export interface SFlowQuery {
  limit?: number;
  direction?: 'incoming' | 'outgoing';
}

/** Recent flow records (`/instances/:id/sflow`), 15s refresh.
 *  `direction` filters traffic direction (default 'outgoing').
 *  `limit` caps flow rows (default 50). */
export function useInstanceSflow(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: SFlowQuery,
) {
  return useQuery({
    queryKey: vmKeys.instanceSflow(serviceId ?? '', instanceId ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 15_000,
    queryFn: async () => {
      const body = unwrap<{ data?: SFlowLog[]; summary?: SFlowSummary }>(
        await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}/sflow`), { params: query }),
      );
      return { logs: body.data ?? [], summary: body.summary ?? {} };
    },
  });
}

/** Aggregated top source IPs (`/instances/:id/sflow_sources`), 15s refresh.
 *  `limit` caps the number of source rows returned (default 20). */
export function useInstanceSflowSources(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: { limit?: number },
) {
  return useQuery({
    queryKey: vmKeys.instanceSflowSources(serviceId ?? '', instanceId ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 15_000,
    queryFn: async () => {
      const body = unwrap<{ sources?: SFlowSource[]; summary?: SFlowSummary; total_unique_sources?: number }>(
        await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}/sflow_sources`), { params: query }),
      );
      return { sources: body.sources ?? [], summary: body.summary ?? {}, total: body.total_unique_sources ?? 0 };
    },
  });
}
